import React from 'react';
import { motion } from 'framer-motion';
import { useCalculatorStore } from '../store/calculatorStore';

export const AnimatedCalculator = () => {
  const { eyeStyle, mouthStyle, accessory, emotion } = useCalculatorStore();

  // Enhanced eye animations based on style
  const getEyeVariants = () => {
    switch (eyeStyle) {
      case 'happy':
        return {
          open: { 
            scaleY: 1,
            scaleX: 1.2,
            y: 0,
            background: '#1e293b',
            borderRadius: '50%'
          },
          blink: { 
            scaleY: 0.2,
            scaleX: 1.2,
            y: 0,
            background: '#1e293b',
            borderRadius: '50%'
          }
        };
      case 'focused':
        return {
          open: { 
            scaleY: 1.2,
            scaleX: 0.8,
            y: -1,
            background: '#1e293b',
            borderRadius: '40%'
          },
          blink: { 
            scaleY: 0.2,
            scaleX: 0.8,
            y: 0,
            background: '#1e293b',
            borderRadius: '40%'
          }
        };
      case 'sleepy':
        return {
          open: { 
            scaleY: 0.5,
            scaleX: 1.1,
            y: 1,
            background: '#1e293b',
            borderRadius: '60%'
          },
          blink: { 
            scaleY: 0.1,
            scaleX: 1.1,
            y: 1,
            background: '#1e293b',
            borderRadius: '60%'
          }
        };
    }
  };

  // Enhanced mouth paths based on style and emotion
  const getMouthPath = () => {
    const baseY = 32;
    switch (mouthStyle) {
      case 'smile':
        return {
          path: `M 26,${baseY} Q 32,${baseY + 6} 38,${baseY}`,
          animate: {
            d: [
              `M 26,${baseY} Q 32,${baseY + 6} 38,${baseY}`,
              `M 26,${baseY} Q 32,${baseY + 7} 38,${baseY}`,
              `M 26,${baseY} Q 32,${baseY + 6} 38,${baseY}`
            ]
          }
        };
      case 'determined':
        return {
          path: `M 26,${baseY} L 38,${baseY}`,
          animate: {
            d: [
              `M 26,${baseY} L 38,${baseY}`,
              `M 26,${baseY + 1} L 38,${baseY - 1}`,
              `M 26,${baseY} L 38,${baseY}`
            ]
          }
        };
      case 'surprised':
        return {
          path: `M 30,${baseY} A 4 4 0 1 1 34,${baseY}`,
          animate: {
            d: [
              `M 30,${baseY} A 4 4 0 1 1 34,${baseY}`,
              `M 30,${baseY} A 4.5 4.5 0 1 1 34,${baseY}`,
              `M 30,${baseY} A 4 4 0 1 1 34,${baseY}`
            ]
          }
        };
    }
  };

  // Enhanced accessories with more detail and animation
  const getAccessory = () => {
    switch (accessory) {
      case 'bowtie':
        return (
          <motion.g
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <motion.path
              d="M 26,40 L 32,38 L 38,40 L 35,43 L 32,41 L 29,43 Z"
              fill="#10b981"
              animate={{
                scale: [1, 1.05, 1],
                rotate: [-2, 2, -2]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.circle
              cx="32"
              cy="39"
              r="1"
              fill="#059669"
              animate={{
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.g>
        );
      case 'glasses':
        return (
          <motion.g
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <motion.path
              d="M 27,25 C 27,22 31,22 31,25 C 31,28 27,28 27,25"
              stroke="#10b981"
              strokeWidth="1.5"
              fill="none"
              animate={{
                strokeWidth: [1.5, 2, 1.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.path
              d="M 33,25 C 33,22 37,22 37,25 C 37,28 33,28 33,25"
              stroke="#10b981"
              strokeWidth="1.5"
              fill="none"
              animate={{
                strokeWidth: [1.5, 2, 1.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.path
              d="M 31,25 L 33,25"
              stroke="#10b981"
              strokeWidth="1.5"
              animate={{
                strokeWidth: [1.5, 2, 1.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.g>
        );
      case 'hat':
        return (
          <motion.g
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <motion.path
              d="M 24,18 Q 32,16 40,18 L 38,14 Q 32,12 26,14 Z"
              fill="#10b981"
              animate={{
                y: [-1, 1, -1],
                rotate: [-2, 2, -2]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.circle
              cx="32"
              cy="16"
              r="1"
              fill="#059669"
              animate={{
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.g>
        );
      default:
        return null;
    }
  };

  const eyeVariants = getEyeVariants();
  const mouthVariants = getMouthPath();

  // Add arm paths with anticipation curve
  const leftArmPath = "M 20,30 Q 35,32 45,31 T 70,30";
  const rightArmPath = "M 10,30 Q 25,32 35,31 T 60,30";

  const armVariants = {
    initial: { 
      pathLength: 0,
      opacity: 0
    },
    animate: {
      pathLength: 1,
      opacity: [0, 1, 1, 1, 1, 0],
      transition: {
        duration: 3,
        ease: [0.4, 0, 0.2, 1],
        repeat: Infinity,
        repeatDelay: 2,
        times: [0, 0.2, 0.4, 0.6, 0.8, 1]
      }
    }
  };

  const fistVariants = {
    initial: { 
      scale: 0,
      opacity: 0,
      rotate: 0
    },
    animate: {
      scale: [0, 1, 1.1, 1, 1, 0],
      opacity: [0, 1, 1, 1, 1, 0],
      rotate: [0, 0, -5, 0, 0, 0],
      transition: {
        duration: 3,
        ease: [0.34, 1.56, 0.64, 1],
        repeat: Infinity,
        repeatDelay: 2,
        times: [0, 0.2, 0.4, 0.5, 0.8, 1]
      }
    }
  };

  // Enhanced fist paths with more detail
  const leftFistPath = `
    M 70,30
    C 72,28 75,27 77,28
    C 78,28.5 78.5,29.5 78,30.5
    C 77.5,31.5 77,32 76,32
    L 75,32.5
    C 73,33 71.5,32 70,30.5
    C 69.5,30 69.5,29.5 70,29
    Z
  `;
  
  const rightFistPath = `
    M 10,30
    C 8,28 5,27 3,28
    C 2,28.5 1.5,29.5 2,30.5
    C 2.5,31.5 3,32 4,32
    L 5,32.5
    C 7,33 8.5,32 10,30.5
    C 10.5,30 10.5,29.5 10,29
    Z
  `;

  return (
    <motion.div
      className="relative w-16 h-16"
      animate={{
        y: [-2, 2, -2],
        rotate: [-1, 1, -1],
      }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl shadow-lg"
        animate={{
          scale: [1, 1.02, 1],
          borderRadius: ["1rem", "1.2rem", "1rem"]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <svg className="absolute inset-0" viewBox="0 0 64 64">
        {/* Sparkle Effects */}
        <motion.circle
          cx="20"
          cy="20"
          r="1"
          fill="#ffffff"
          animate={{
            scale: [0, 1, 0],
            opacity: [0, 0.5, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 1
          }}
        />
        <motion.circle
          cx="44"
          cy="20"
          r="1"
          fill="#ffffff"
          animate={{
            scale: [0, 1, 0],
            opacity: [0, 0.5, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 1.5
          }}
        />

        {/* Eyes with enhanced animations */}
        <g>
          <motion.circle
            cx="26"
            cy="26"
            r="3"
            fill="#1e293b"
            variants={eyeVariants}
            initial="open"
            animate={{
              ...eyeVariants.open,
              scale: [1, 1, 0.2, 1],
              transition: {
                duration: 0.3,
                repeat: Infinity,
                repeatDelay: 3,
              }
            }}
          />
          <motion.circle
            cx="38"
            cy="26"
            r="3"
            fill="#1e293b"
            variants={eyeVariants}
            initial="open"
            animate={{
              ...eyeVariants.open,
              scale: [1, 1, 0.2, 1],
              transition: {
                duration: 0.3,
                repeat: Infinity,
                repeatDelay: 3,
              }
            }}
          />
        </g>

        {/* Enhanced Mouth */}
        <motion.path
          d={mouthVariants.path}
          stroke="#1e293b"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          animate={mouthVariants.animate}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Arms */}
        <motion.path
          d={leftArmPath}
          stroke="url(#buddyGradient)"
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
          variants={armVariants}
          initial="initial"
          animate="animate"
        />
        <motion.path
          d={leftFistPath}
          fill="url(#buddyGradient)"
          variants={fistVariants}
          initial="initial"
          animate="animate"
        />
        <motion.path
          d={rightArmPath}
          stroke="url(#buddyGradient)"
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
          variants={armVariants}
          initial="initial"
          animate="animate"
        />
        <motion.path
          d={rightFistPath}
          fill="url(#buddyGradient)"
          variants={fistVariants}
          initial="initial"
          animate="animate"
        />

        {/* Accessories */}
        {getAccessory()}

        {/* Gradients */}
        <defs>
          <linearGradient id="buddyGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
      </svg>

      {/* Enhanced Display Screen */}
      <motion.div
        className="absolute top-2 left-3 right-3 h-3 bg-emerald-200/90 rounded"
        animate={{
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <motion.div
          className="absolute inset-0 bg-emerald-400/20 rounded blur-sm"
          animate={{
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>

      {/* Enhanced Calculator Buttons */}
      <div className="absolute bottom-2 left-3 right-3 grid grid-cols-3 gap-1">
        {[1, 2, 3, 4, 5, 6].map((_, index) => (
          <motion.div
            key={index}
            className="h-1.5 w-1.5 bg-emerald-300/80 rounded-full"
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.8 }}
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.8, 1, 0.8]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: index * 0.2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Enhanced Ambient Glow */}
      <motion.div
        className="absolute -inset-3 bg-emerald-400/10 rounded-full blur-xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </motion.div>
  );
};